import type { AuthSessionTokens } from "./auth.type";
import type { ApiAuthConfigs } from "@/services/api";
import type { ResourcesConfig } from "aws-amplify";
import type { AuthTokens } from "aws-amplify/auth";

import { Amplify } from "aws-amplify";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import { jwtDecode } from "jwt-decode";

import { isDebugModeActivated } from "@/config/environmentUtils";
import { formatDuration } from "@/utilities/date";

export const isValidToken = (token?: string) => {
  if (!token) {
    return false;
  }

  try {
    const { exp } = jwtDecode(token);
    const expireAt = new Date((exp as number) * 1000);
    const remainingMilliseconds = expireAt.getTime() - Date.now();

    return remainingMilliseconds > 0;
  } catch (_error) {
    // example: Invalid token specified
    return false;
  }
};

export const getOriginLocation = () => window?.location?.origin ?? "";

export const buildAuthConfigurations = (
  config: ApiAuthConfigs
): ResourcesConfig => {
  const redirectURL = getOriginLocation();
  const { ClientId, OAuthDomain, UserPoolId } = config ?? {};

  return {
    Auth: {
      Cognito: {
        userPoolId: UserPoolId,
        userPoolClientId: ClientId,
        signUpVerificationMethod: "link", // 'code' is used for Auth.confirmSignUp, 'link' is used for email link verification
        loginWith: {
          oauth: {
            domain: OAuthDomain,
            scopes: ["email", "openid"],
            redirectSignIn: [redirectURL],
            redirectSignOut: [redirectURL],
            responseType: "code", // or 'token', note that REFRESH token will only be generated when the responseType is code
          },
        },
        mfa: {
          status: "off",
          smsEnabled: true,
        },
        passwordFormat: {
          minLength: 8,
        },
      },
    },
  };
};

const REFRESH_TOKEN_DELTA_IN_SECONDS = 60;

export const getConfiguredAuthDomain = () => {
  return Amplify.getConfig()?.Auth?.Cognito?.loginWith?.oauth?.domain ?? "";
};

export const isOAuthConfigured = () => !!getConfiguredAuthDomain();

export const formatSessionTokens = (tokens?: AuthTokens): AuthSessionTokens => {
  if (!tokens) return {};

  return {
    accessToken: tokens.accessToken?.toString(),
    idToken: tokens.idToken?.toString(),
  };
};

// https://docs.amplify.aws/javascript/build-a-backend/auth/manage-user-session/#retrieve-your-current-authenticated-user
export const getCurrentAuthenticatedUser = async () => {
  try {
    const currentUser = await getCurrentUser();
    logAuthEvent("getCurrentAuthenticatedUser: success", currentUser);
    return currentUser;
  } catch (err) {
    logAuthEvent("getCurrentAuthenticatedUser: error", err);
  }
};

// https://docs.amplify.aws/javascript/build-a-backend/auth/manage-user-session/#retrieve-a-user-session
export const getCurrentSession = async () => {
  try {
    const session = await fetchAuthSession();
    const tokens = formatSessionTokens(session.tokens);
    logAuthEvent("getCurrentSession: success", { session, tokens });
    return { session, tokens };
  } catch (err) {
    logAuthEvent("getCurrentSession: error", err);
  }
};

// https://docs.amplify.aws/javascript/build-a-backend/auth/manage-user-session/#refreshing-sessions
export const refreshCurrentSession = async () => {
  try {
    // We use `forceRefresh: true` here to make sure a new token is
    // fetched. It ensures `useEffect` in `useAuthRefreshToken` is
    // re-runed and verify if token after token valid time is expired.
    const session = await fetchAuthSession({ forceRefresh: true });
    const tokens = formatSessionTokens(session.tokens);
    logAuthEvent("refreshCurrentSession: success", { session, tokens });
    return { session, tokens };
  } catch (err) {
    logAuthEvent("refreshCurrentSession: error", err);
  }
};

export const loadAuthSessionInfo = async () => {
  if (!isOAuthConfigured()) {
    logAuthEvent("loadAuthSessionInfo: auth domain not configured yet");
    return;
  }

  try {
    logAuthEvent("loadAuthSessionInfo: loading user session (if any)");
    const user = await getCurrentAuthenticatedUser();
    const { session, tokens } = (await getCurrentSession()) ?? {};
    logAuthEvent("loadAuthSessionInfo: tokens", buildTokensStats(tokens));
    return { user, session, tokens };
  } catch (err) {
    logAuthEvent(
      "loadAuthSessionInfo: user not successfuly authenticated yet",
      err
    );
  }
};

export const refreshAccessToken = async () => {
  if (!isOAuthConfigured()) {
    logAuthEvent("refreshAccessToken: auth domain not configured yet");
    return;
  }

  try {
    const { tokens } = (await refreshCurrentSession()) ?? {};
    logAuthEvent("refreshAccessToken: success", buildTokensStats(tokens));
    return tokens;
  } catch (err) {
    logAuthEvent("refreshAccessToken: error", err);
  }
};

export const getTokenRefreshTimeout = (token: string) => {
  const decodedTokenInfo = jwtDecode(token);
  const refreshSessionAt = new Date(
    ((decodedTokenInfo.exp as number) - REFRESH_TOKEN_DELTA_IN_SECONDS) * 1000
  );

  const timeoutDuration = Math.max(refreshSessionAt.getTime() - Date.now(), 0);
  logAuthEvent(`getTokenRefreshTimeout: ${formatDuration(timeoutDuration)}`);

  return timeoutDuration;
};

export const logAuthEvent = (message = "", ...args: unknown[]) => {
  if (isDebugModeActivated("auth")) {
    const authDomain =
      getConfiguredAuthDomain() || "auth-domain-not-configured";
    console.info(`[AUTH ${authDomain}] ${message}`, ...args);
  }
};

export const buildTokensStats = (tokens?: AuthSessionTokens) => {
  const { accessToken, idToken } = tokens ?? {};

  return {
    hasAccessToken: !!accessToken,
    hasIdToken: !!idToken,
  };
};
